<template>
  <div class="training">
    <div class="training__fields" :class="{ 'training__fields--web': isWeb }">
      <p-form ref="trainingForm"
              :form-data="formData"
              :class-name="'form--web'"
              :disable-form="isDisabled"
              :disable-buttons="true"/>
    </div>
    <div v-if="yearsValid"
         class="training__valid"
         :class="{ 'training__valid--web': isWeb }">
      Dit certificaat / bewijs van deelname is bij PIDZ {{ yearsValid }} jaar
      geldig
    </div>
    <div class="training__files"
         :class="[
           { 'training__files--web': isWeb },
           { 'training__files--disabled': isDisabled },
         ]">
      <p-expanded-picker :new-files="newFiles"
                         :existing-files="existingAssets"
                         :disabled="isDisabled"
                         :show-status="false"
                         @upload="onAddFile"
                         @delete="onDeleteFile"
                         @download="onDownloadFile"/>
      <p v-if="showFileError" class="file__error">
        Je moet voor deze aanvullende scholing een bestand toevoegen
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useFormFunctions } from '@pidz/shared/forms';
import {
  subjectField,
  TRAINING_ASSET_TYPE_ID,
  setYearsValid,
  toggleOtherSubjectsField,
  trainingOptions,
  resetOtherSubjectField,
} from '@pidz/shared/forms/cv/training';
import { rerouteToOverview, fileRemove, fileUpload } from '@pidz/shared/utils';
import { useCVStore, useAssetStore } from '@pidz/stores';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  useRouter,
  Router,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';

interface Props {
  trainingItem?: Training;
  isWeb?: boolean;
  isEdit?: boolean;
  formData: PidzFormData<TrainingForm> | PidzFormData<NewTrainingForm>;
  isDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isWeb: false,
  isEdit: false,
  isDisabled: false,
});

const trainingForm = ref<any>();

const router: Router = useRouter();
const cvStore = useCVStore();
const assetStore = useAssetStore();
const showFiles = ref<boolean>(true);
const yearsValid = ref<null | number>(null);

const showFileError = computed(() => {
  return (
    (newFiles.value.length === 0 &&
      existingAssets.value.length < 1 &&
      trainingForm?.value?.submitCount > 0 &&
      showFiles.value) ||
    (deletedFiles.value.length ===
      (props?.formData?.values?.assets || []).length &&
      showFiles.value &&
      !(newFiles.value.length > 0))
  );
});

const itemId = props.trainingItem?.id ?? 0;

const formSubmitted = ref(false);
const eventName = props.isEdit ? 'cv_modal_edit_cancel' : 'cv_modal_add_cancel';

onBeforeRouteLeave((_to, _from, next) =>
  warnBeforeCancel(next, formSubmitted.value, props.isWeb, eventName),
);
onBeforeRouteUpdate((_to, _from, next) =>
  warnBeforeCancel(next, formSubmitted.value, props.isWeb, eventName),
);

const {
  isSubmitting,
  isSubmitDisabled,
  succesToaster,
  onCancel,
  onDelete,
  newFiles,
  deletedFiles,
  existingAssets,
  onAddFile,
  onDownloadFile,
  onDeleteFile,
  warnBeforeCancel,
} = useFormFunctions(
  trainingForm,
  props.trainingItem?.assets,
  () => rerouteToOverview(router, 'cv'),
  async () => {
    props.trainingItem?.assets.forEach(
      async (asset: Asset) => await assetStore.removeAsset(asset.id!),
    );
    await cvStore.removeTraining(itemId);
    succesToaster('Succesvol verwijderd');
  },
  props.isEdit,
  'aanvullende scholing',
);

const onSubmit = async () => {
  const submit = trainingForm?.value?.handleSubmit(
    async (formValues: TrainingForm) => {
      if (
        trainingForm?.value?.isValidating ||
        !trainingForm?.value?.meta.valid ||
        showFileError.value
      )
        return;

      const files = await fileUpload(newFiles.value, TRAINING_ASSET_TYPE_ID);
      if (deletedFiles.value.length > 0 && props.isEdit)
        fileRemove(deletedFiles.value);

      const { subject, ...training } = formValues;

      let item: AddTrainingParams = {
        ...training,
        subject: {
          id: subject,
        },
        assets: props.isEdit ? [...existingAssets.value, ...files] : files,
      };

      let editItem: UpdateTrainingParams = {
        ...item,
        id: itemId,
      };

      props.isEdit
        ? await cvStore.updateTraining(editItem)
        : await cvStore.addTraining(item);

      formSubmitted.value = true;
      rerouteToOverview(router, 'cv');
      succesToaster();
    },
  );

  if (submit) {
    await submit();
  }
};

onMounted(async () => {
  await cvStore.loadTrainingSubjects();
  subjectField.value.data.options = trainingOptions(
    cvStore.trainingSubjects || [],
  );
});

watch(
  () => trainingForm?.value?.values?.subject,
  (value) => {
    yearsValid.value = setYearsValid(value);
    toggleOtherSubjectsField(
      value,
      trainingForm,
      props.formData.values.other_subject || '',
    );
  },
);

onBeforeUnmount(() => resetOtherSubjectField());

defineExpose({
  isSubmitDisabled,
  isSubmitting,
  onCancel,
  onSubmit,
  onDelete,
  onDownloadFile,
  showFileError,
});
</script>
<style scoped lang="scss">
.training {
  &__valid {
    background-color: var(--pidz-white);
    color: var(--pidz-body-text);
    box-shadow: var(--shadow-light);
    padding: 24px 16px;
    padding-top: 0px;

    &--web {
      padding: 0 32px 32px 32px;
    }
  }
  &__fields {
    background-color: var(--pidz-white);
    color: var(--pidz-body-text);
    box-shadow: var(--shadow-light);
    padding: 24px 16px;

    &--web {
      padding: 32px;
      display: flex;
      flex-direction: column;
      padding-bottom: 0;
      box-shadow: none;
    }
  }
  &__files {
    background-color: var(--pidz-white);
    color: var(--pidz-body-text);
    box-shadow: var(--shadow-light);
    padding: 24px 16px;
    padding-top: 0;

    &--web {
      padding: 32px;
      padding-top: 0;
      box-shadow: none;
    }
    &--disabled {
      padding-bottom: 0;
    }
  }
}
.file {
  &__error {
    font-size: 14px;
    color: red;
  }
}
</style>
